import { CSSResult, html, LitElement, nothing, TemplateResult, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { consume } from '@lit/context';
import filterIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_filter.svg?element';
import { Messages } from '../../../@types/wishlist.js';
import styles from './filter-button.scss?inline';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { FilterMenu } from '../../../scripts/filter/filter-menu.js';
import { filterContext } from '../../../scripts/context/filterContext.js';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { OptionUIModel } from '../../../scripts/filter/option-ui-model.js';
import { repeat } from 'lit/directives/repeat.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// lazy load initially hidden components
import('./filter-overview.js');
import('./filter-group.js');

@customElement('ents-filter-button')
export class UIFilter extends LitElement {
  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: filterContext, subscribe: true })
  @property({ attribute: false })
  private filterMenu!: FilterMenu;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  private setLayerOpen() {
    this.dispatchEvent(new Event('open'));
  }

  private activateFilterOption(optionId: string, allowRemove: boolean) {
    this.dispatchEvent(
      new CustomEvent('toggle-and-apply', { detail: { optionId, allowRemove }, bubbles: true, composed: true }),
    );
    this.dispatchEvent(new Event('scroll-top'));
  }

  private renderOneClickOptions() {
    const staticOptions = this.filterMenu.getStaticOneClickOptions();
    const staticOptionIds = new Set(staticOptions.map((option) => option.id));
    const activeOptions = this.filterMenu.isScoringEnabled
      ? this.filterMenu.filter
          .filter((filter) => !filter.isSort)
          .flatMap((filter) => filter.getAppliedActiveAndNotDefaultOptions())
          .filter((option) => !staticOptionIds.has(option.id))
      : [];

    const prioritizedOptions = [...staticOptions, ...activeOptions];
    const limit = prioritizedOptions.length === 0 ? 4 : Math.min(3, 6 - prioritizedOptions.length);
    const recommendedOptions =
      limit > 0 && this.filterMenu.isScoringEnabled ? this.filterMenu.getRecommendedOneClickOptions(limit) : [];

    return html`
      ${repeat(
        prioritizedOptions,
        (prioritizedOption) => `prioritized-${prioritizedOption.id}`,
        (prioritizedOption) => this.renderOption('prioritized', prioritizedOption),
      )}
      ${repeat(
        recommendedOptions,
        (recommendedOption) => `recommended-${recommendedOption.option.id}`,
        (recommendedOption) =>
          this.renderOption(
            'recommended',
            recommendedOption.option,
            `(${Math.round(recommendedOption.score * 1000) / 1000})`,
          ),
      )}
    `;
  }

  private renderOption(type: string, option: OptionUIModel, additionalLabel: string | undefined = undefined) {
    return html`
      <button
        variant="secondary"
        size="medium"
        class="bdk-chip"
        type="button"
        data-test-ents-filter-button-id="${type}-${option.id}"
        @click="${() => this.activateFilterOption(option.id, true)}"
        ?active="${option.isActive(false)}"
      >
        <span class="ents-filter-button__button__label">
          ${unsafeHTML(option.shortLabel(this.messages))} ${ifDefined(additionalLabel)}
        </span>
      </button>
    `;
  }

  protected render(): TemplateResult {
    const activeFilterCount = this.filterMenu.getAppliedActiveAndNotDefaultOptionsCount(true);

    return html`
      <div class="ents-filter-buttons">
        <div class="ents-filter-button" data-test-ents-filter-button-shadow>
          <button
            class="bdk-chip"
            variant="secondary"
            size="medium"
            type="button"
            @click="${this.setLayerOpen}"
            aria-label="${this.messages['ents_wishlist_filter_button_description']}"
            ?active="${activeFilterCount > 0}"
          >
            ${hideIconFromAssistiveTechnology(filterIcon())}
            <span class="ents-filter-button__button__label ents-filter-button__button__label--all"
              >${this.messages['ents_wishlist_filter_button_all']}</span
            >
            ${activeFilterCount > 0
              ? html`
                  <div class="bdk-badge-count" size="small" data-test-ents-filter-button-count="${activeFilterCount}">
                    ${activeFilterCount}
                  </div>
                `
              : nothing}
          </button>
        </div>
        ${this.renderOneClickOptions()}
      </div>
    `;
  }
}
